import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid2,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TimflowSteadyForm } from "../../types/forms/capture";
import { TimflowSteadyResult } from "../../types/interfaces/methods";
import {
  TimflowSteadyCsvQuerySchema,
  TimflowSteadyRequest,
  TimflowSteadyRequestSchema,
} from "../../types/model/calculation";
import { CaptureResponse } from "../../types/model/capture";
import { MethodsInput } from "../../types/model/methodsInput";
import { fromTimflowPayload } from "../../utils/Methods/fromTimflowPayload";
import {
  CAPTURE_ZONE_DEFAULTS,
  CreateInputWithFlow,
  MODEL_DEFAULTS,
  SaveInputFlow,
  UsedWell,
  getFlow,
  hasValidFlow,
  isEmptyNumber,
  placeholderOf,
} from "../../utils/Methods/Timflow";
import { buildTimflowPayload } from "../../utils/Methods/timflowSteady";
import NumberField from "./Timflow/Numberfield";
import TimflowSummaryGrid from "./Timflow/TimflowSummaryGrid";
import WellsTable from "./Timflow/WellsTable";

interface CaptureTimflowSteadyProps {
  captureId: number;
  canEdit: boolean;
  captures: CaptureResponse[];
  methodsInputsByCapture: Record<number, MethodsInput[]>;
  isLoadingInputs: boolean;
  onWellCapturesChange: (captureIds: number[]) => void;
  onSaveInputFlow: SaveInputFlow;
  onCreateInputWithFlow: CreateInputWithFlow;
  result: TimflowSteadyResult | null;
  isCalculating: boolean;
  isDownloading: boolean;
  onCalculate: (
    captureId: number,
    inputId: number,
    payload: TimflowSteadyRequest,
    days: number,
  ) => Promise<void>;
  onDownloadCsv: (
    captureId: number,
    inputId: number,
    payload: TimflowSteadyRequest,
    days: number,
  ) => Promise<void>;
  onLoadResult: (captureId: number, inputId: number) => Promise<void>;
}

export default function CaptureTimflowSteady({
  captureId,
  canEdit,
  captures,
  methodsInputsByCapture,
  isLoadingInputs,
  onWellCapturesChange,
  onSaveInputFlow,
  onCreateInputWithFlow,
  result,
  isCalculating,
  isDownloading,
  onCalculate,
  onDownloadCsv,
  onLoadResult,
}: Readonly<CaptureTimflowSteadyProps>) {
  const { t } = useTranslation();
  const [formError, setFormError] = useState<string | null>(null);
  const tf = (k: string) => t(`calculations.methods.timflow.fields.${k}`);

  const methods = useForm<TimflowSteadyForm>({
    defaultValues: {
      model: { z: [{}, {}], topboundary: "conf" },
      uflowEnabled: false,
      uflow: {},
      wells: [{ captureId, inputId: "" }],
      constants: [],
      recharge: [],
      rivers: [],
      captureZone: { silent: "." },
      days: 50,
    },
    shouldFocusError: false,
  });
  const { control, watch, handleSubmit, getValues, setValue } = methods;

  const zArr = useFieldArray({ control, name: "model.z" });
  const wellsArr = useFieldArray({ control, name: "wells" });

  const uflowEnabled = watch("uflowEnabled");
  const isSemi = watch("model.topboundary") === "semi";
  const wells = watch("wells");
  const wellCaptureIds = wells.map((w) => w.captureId);
  const availableCaptures = captures.filter(
    (c) => !wellCaptureIds.includes(c.id),
  );

  const firstWell = wells[0];
  const firstInputId = firstWell?.inputId;

  useEffect(() => {
    onWellCapturesChange(wellCaptureIds);
  }, [wellCaptureIds.join(",")]);

  useEffect(() => {
    if (!firstWell || !firstInputId) return;
    onLoadResult(firstWell.captureId, Number(firstInputId));
  }, [firstInputId]);

  useEffect(() => {
    if (!result) return;

    const saved = (result as any).inputParameters;

    if (!saved) return;

    const next = fromTimflowPayload(saved, getValues());

    methods.reset(next);
  }, [result]);

  useEffect(() => {
    if (!result) return;

    const usedWells = (result.results as { wells?: UsedWell[] }).wells ?? [];

    getValues("wells").forEach((w, i) => {
      const used = usedWells.find(
        (u) => u.captureId === w.captureId && u.inputId === w.inputId,
      );
      if (!used) return;

      if (isEmptyNumber(w.rw) && used.rw != null)
        setValue(`wells.${i}.rw`, used.rw);
      if (isEmptyNumber(w.res) && used.res != null)
        setValue(`wells.${i}.res`, used.res);
    });

    const aquifers = (result.results.aquiferSummary ?? []).filter(
      (l) => l.layerType === "aquifer",
    );
    if (aquifers.length === 1 && isEmptyNumber(getValues("model.kaq"))) {
      const H = Number(aquifers[0].H);
      const kH = Number(aquifers[0].kH);
      if (Number.isFinite(H) && Number.isFinite(kH) && H > 0) {
        setValue("model.kaq", kH / H);
      }
    }
  }, [result]);

  const submit = (mode: "calculate" | "csv") =>
    handleSubmit(async (values) => {
      const z = values.model.z.map((p) => p.value);
      const filled = z.every((v) => v !== undefined && !Number.isNaN(v));

      if (filled && z.some((v, i) => i > 0 && v! >= z[i - 1]!)) {
        setFormError(t("calculations.methods.timflow.errors.z-order"));
        return;
      }

      const wellWithoutFlow = values.wells.find((w) => {
        const input = (methodsInputsByCapture[w.captureId] ?? []).find(
          (mi) => mi.id === w.inputId,
        );
        return input && !hasValidFlow(getFlow(input));
      });

      if (wellWithoutFlow) {
        const name =
          captures.find((c) => c.id === wellWithoutFlow.captureId)?.name ??
          `#${wellWithoutFlow.captureId}`;
        setFormError(
          t("calculations.methods.timflow.errors.missing-flow", {
            capture: name,
            param: t("calculations.methods-input.flow-m3-day"),
          }),
        );
        return;
      }

      const query = TimflowSteadyCsvQuerySchema.safeParse({
        days: values.days,
      });

      if (!query.success) {
        setFormError(t("calculations.methods.timflow.errors.days-positive"));
        return;
      }

      setFormError(null);

      const parsed = TimflowSteadyRequestSchema.safeParse(
        buildTimflowPayload(values),
      );

      if (!parsed.success) {
        setFormError(
          parsed.error.issues
            .map((i) => `${i.path.join(".")}: ${i.message}`)
            .join(" | "),
        );
        return;
      }

      const payload = parsed.data;
      const { captureId: routeCaptureId, inputId: routeInputId } =
        payload.wells[0];

      if (mode === "calculate") {
        await onCalculate(
          routeCaptureId,
          routeInputId,
          payload,
          query.data.days,
        );
        return;
      }

      await onDownloadCsv(
        routeCaptureId,
        routeInputId,
        payload,
        query.data.days,
      );
    });

  const calculationRows = (result?.results.calculations ?? []).map(
    ({ captureId: cId, inputId: iId, ...rest }) => ({
      capture: captures.find((c) => c.id === cId)?.name ?? `#${cId}`,
      input:
        (methodsInputsByCapture[cId] ?? []).find((mi) => mi.id === iId)?.name ??
        `#${iId}`,
      ...rest,
    }),
  );

  return (
    <FormProvider {...methods}>
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Stack spacing={3}>
          <Typography variant="h6">
            {t("calculations.methods.timflow.model")}
          </Typography>
          <Grid2 container spacing={2} alignItems="flex-start">
            <Grid2 size={{ xs: 12, sm: 3 }}>
              <NumberField
                name="model.kaq"
                label={tf("kaq")}
                placeholder={placeholderOf(MODEL_DEFAULTS.kaq)}
                disabled={!canEdit}
                unit="m-per-day"
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 3 }}>
              <NumberField
                name="model.npor"
                label={tf("npor")}
                placeholder={placeholderOf(MODEL_DEFAULTS.npor)}
                disabled={!canEdit}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 3 }}>
              <Controller
                control={control}
                name="model.topboundary"
                render={({ field }) => (
                  <TextField
                    select
                    label={tf("topboundary")}
                    disabled={!canEdit}
                    fullWidth
                    {...field}
                  >
                    <MenuItem value="conf">conf</MenuItem>
                    <MenuItem value="semi">semi</MenuItem>
                  </TextField>
                )}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 3 }}>
              <NumberField
                name="model.hstar"
                label={tf("hstar")}
                placeholder={placeholderOf(MODEL_DEFAULTS.hstar)}
                helperText={
                  isSemi
                    ? undefined
                    : t("calculations.methods.timflow.helpers.hstar-semi-only")
                }
                disabled={!canEdit || !isSemi}
                unit="m"
              />
            </Grid2>
          </Grid2>

          <Stack spacing={1}>
            <Typography variant="subtitle2" color="text.secondary">
              {t("calculations.methods.timflow.helpers.z-title")}
            </Typography>
            <Grid2 container spacing={2} alignItems="center">
              {zArr.fields.map((f, i) => (
                <Grid2 key={f.id} size={{ xs: 6, sm: 3 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <NumberField
                      name={`model.z.${i}.value`}
                      label={i === 0 ? tf("z-top") : `${tf("z")} [${i}]`}
                      placeholder={placeholderOf(MODEL_DEFAULTS.z)}
                      disabled={!canEdit}
                      unit="m"
                    />
                    {canEdit && zArr.fields.length > 2 && (
                      <Tooltip title={t("generic.delete")}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => zArr.remove(i)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Grid2>
              ))}
              {canEdit && (
                <Grid2>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => zArr.append({})}
                  >
                    z
                  </Button>
                </Grid2>
              )}
            </Grid2>
          </Stack>

          <Divider />

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              opacity: uflowEnabled ? 1 : 0.7,
              transition: "opacity 0.2s",
            }}
          >
            <Grid2 container spacing={2} alignItems="flex-start">
              <Grid2 size={{ xs: 12, sm: 3 }}>
                <Controller
                  control={control}
                  name="uflowEnabled"
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ mt: { sm: 0.5 } }}
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          disabled={!canEdit}
                        />
                      }
                      label={
                        <Typography variant="subtitle1" fontWeight={600}>
                          Uflow
                        </Typography>
                      }
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 4.5 }}>
                <NumberField
                  name="uflow.slope"
                  label={tf("slope")}
                  disabled={!canEdit || !uflowEnabled}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 4.5 }}>
                <NumberField
                  name="uflow.angle"
                  label={tf("angle")}
                  disabled={!canEdit || !uflowEnabled}
                  unit="degrees"
                />
              </Grid2>
            </Grid2>
          </Paper>

          <Divider />

          <WellsTable
            canEdit={canEdit}
            captures={captures}
            availableCaptures={availableCaptures}
            methodsInputsByCapture={methodsInputsByCapture}
            isLoadingInputs={isLoadingInputs}
            wellsArr={wellsArr}
            onSaveInputFlow={onSaveInputFlow}
            onCreateInputWithFlow={onCreateInputWithFlow}
          />

          <Divider />

          <Typography variant="h6">
            {t("calculations.methods.timflow.capture-zone")}
          </Typography>
          <Grid2 container spacing={2} alignItems="flex-start">
            <Grid2 size={{ xs: 12, sm: 4 }}>
              <NumberField
                name="captureZone.nt"
                label={tf("nt")}
                step={1}
                min={1}
                placeholder={placeholderOf(CAPTURE_ZONE_DEFAULTS.nt)}
                disabled={!canEdit}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 4 }}>
              <NumberField
                name="captureZone.zstart"
                label={tf("zstart")}
                placeholder={placeholderOf(CAPTURE_ZONE_DEFAULTS.zstart)}
                helperText={t(
                  "calculations.methods.timflow.helpers.zstart-empty",
                )}
                disabled={!canEdit}
                unit="m"
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 4 }}>
              <Controller
                control={control}
                name="captureZone.silent"
                render={({ field }) => (
                  <TextField
                    select
                    label={tf("silent")}
                    disabled={!canEdit}
                    fullWidth
                    {...field}
                  >
                    <MenuItem value="true">
                      {t("calculations.methods.timflow.silent-options.true")}
                    </MenuItem>
                    <MenuItem value="false">
                      {t("calculations.methods.timflow.silent-options.false")}
                    </MenuItem>
                    <MenuItem value=".">
                      {t("calculations.methods.timflow.silent-options.dot")}
                    </MenuItem>
                  </TextField>
                )}
              />
            </Grid2>
          </Grid2>

          <Typography variant="subtitle2" color="text.secondary">
            {t("calculations.methods.timflow.helpers.steps-title")}
          </Typography>
          <Grid2 container spacing={2} alignItems="flex-start">
            <Grid2 size={{ xs: 12, sm: 4 }}>
              <NumberField
                name="captureZone.hstepmax"
                label={tf("hstepmax")}
                min={0}
                placeholder={placeholderOf(CAPTURE_ZONE_DEFAULTS.hstepmax)}
                disabled={!canEdit}
                unit="m"
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 4 }}>
              <NumberField
                name="captureZone.vstepfrac"
                label={tf("vstepfrac")}
                min={0}
                placeholder={placeholderOf(CAPTURE_ZONE_DEFAULTS.vstepfrac)}
                disabled={!canEdit}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 4 }}>
              <NumberField
                name="captureZone.nstepmax"
                label={tf("nstepmax")}
                step={1}
                min={1}
                placeholder={placeholderOf(CAPTURE_ZONE_DEFAULTS.nstepmax)}
                disabled={!canEdit}
              />
            </Grid2>
          </Grid2>

          {formError && <Alert severity="error">{formError}</Alert>}

          <Stack
            direction="row"
            spacing={2}
            alignItems="flex-start"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
          >
            <Button
              variant="contained"
              sx={{ minWidth: 160, height: 40 }}
              disabled={!canEdit || isCalculating}
              onClick={submit("calculate")}
            >
              {isCalculating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t("generic.calculate")
              )}
            </Button>

            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ width: 120 }}>
                <NumberField
                  name="days"
                  unit="days"
                  label={tf("days")}
                  size="small"
                />
              </Box>
              <Button
                variant="outlined"
                sx={{ height: 40 }}
                startIcon={
                  isDownloading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DownloadIcon />
                  )
                }
                disabled={isDownloading}
                onClick={submit("csv")}
              >
                CSV
              </Button>
            </Stack>
          </Stack>

          {result && (
            <>
              <TimflowSummaryGrid
                title={t("calculations.methods.timflow.aquifer-summary")}
                rows={result.results.aquiferSummary}
              />
              <TimflowSummaryGrid
                title={t("calculations.methods.timflow.calculations")}
                rows={calculationRows}
              />
            </>
          )}
        </Stack>
      </Paper>
    </FormProvider>
  );
}
